import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Car,
  Fuel,
  ShieldCheck,
  Sparkles,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { HeroWizard } from "@/components/home/HeroWizard";
import { FeaturedVehicles } from "@/components/home/FeaturedVehicles";
import { Button } from "@/components/ui/button";
import { useGlobalState } from "@/hooks/context/global/useGlobalState";
import { AnimatedBackground } from "@/components/AnimatedBackground";
import { Vehicle } from "@/types";

type QuickLink = {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  gradient: string;
  /** Ruta interna del catálogo (con filtros). */
  to?: string;
  /** Enlace externo (se abre en pestaña nueva). */
  href?: string;
};

const quickLinks: QuickLink[] = [
  {
    title: "Motocicletas Nuevas",
    subtitle: "Modelos de última generación",
    to: "/catalogo?type=Motocicleta&condition=Nuevo",
    icon: Sparkles,
    gradient: "from-[#E10E19] to-[#C40D17]",
  },
  {
    title: "Motocicletas Usadas",
    subtitle: "Opciones certificadas",
    to: "/catalogo?type=Motocicleta&condition=Usado",
    icon: ShieldCheck,
    gradient: "from-[#39367D] to-[#1F1D4A]",
  },
  {
    title: "Automóviles",
    subtitle: "Carros para ciudad y trabajo",
    to: "/catalogo?type=Carro",
    icon: Car,
    gradient: "from-[#1E40AF] to-[#2563EB]",
  },
  {
    title: "Putoline",
    subtitle: "Lubricantes y cuidado premium",
    href: "https://www.putoline.com",
    icon: Fuel,
    gradient: "from-[#111827] to-[#374151]",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { setSelectedVehicle, brands } = useGlobalState();

  const handleVehicleClick = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    navigate(`/vehiculo/${vehicle.id}`);
  };

  const handleQuickLink = (link: QuickLink) => {
    if (link.href) {
      window.open(link.href, "_blank", "noopener,noreferrer");
    } else if (link.to) {
      navigate(link.to);
    }
  };

  return (
    <div className="relative -mt-16 min-h-screen overflow-x-clip bg-transparent lg:-mt-20">
      <AnimatedBackground />
      <HeroWizard brands={brands} />

      <main className="relative z-10 mx-auto w-full max-w-[1400px] px-3 pb-12 pt-6 sm:px-6 sm:pb-20 lg:px-10">
        <section className="mb-8 grid gap-4 lg:grid-cols-[1.2fr_1fr] lg:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            className="relative overflow-hidden rounded-3xl border border-white/20 bg-linear-to-br from-[#39367D] via-[#2E2B66] to-[#16142D] p-6 text-white shadow-2xl sm:p-8 md:p-10"
          >
            <div className="absolute -right-24 -top-20 h-56 w-56 rounded-full bg-[#E10E19]/30 blur-3xl" />
            <div className="absolute -left-20 bottom-0 h-52 w-52 rounded-full bg-[#2563EB]/25 blur-3xl" />

            <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em]">
              <Wrench className="h-3.5 w-3.5" />
              Oriental Ramirez
            </p>

            <h1 className="max-w-[14ch] text-4xl font-black leading-[0.9] sm:text-5xl md:text-6xl">
              Tu proximo
              <span className="block text-[#FFD44D]">vehiculo</span>
              empieza aqui.
            </h1>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                className="h-11 rounded-full bg-[#E10E19] px-6 text-white hover:bg-[#C40D17]"
                onClick={() => navigate("/catalogo")}
              >
                Ver Catalogo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-11 rounded-full border-white/35 bg-transparent px-6 text-white hover:bg-white/10 hover:text-white"
                onClick={() => navigate("/financiamiento")}
              >
                Financiamiento
              </Button>
            </div>
          </motion.div>

          <div className="grid gap-4 sm:grid-cols-2">
            {quickLinks.map((link, idx) => {
              const Icon = link.icon;
              return (
                <motion.button
                  key={link.title}
                  type="button"
                  onClick={() => handleQuickLink(link)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: idx * 0.08 }}
                  className={`group relative overflow-hidden rounded-2xl border border-white/20 bg-linear-to-br p-5 text-left text-white shadow-xl ${link.gradient}`}
                >
                  <div className="absolute -right-10 -top-12 h-32 w-32 rounded-full bg-white/15 blur-2xl transition-transform duration-500 group-hover:scale-125" />
                  <Icon className="mb-4 h-6 w-6 text-white/90" />
                  <h3 className="text-lg font-bold leading-tight">{link.title}</h3>
                  <p className="mt-1 text-sm text-white/80">{link.subtitle}</p>
                  <span className="mt-4 inline-flex items-center text-xs font-semibold uppercase tracking-wider text-white/90">
                    Explorar
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </section>

        <section className="relative left-1/2 right-1/2 mb-10 w-screen -translate-x-1/2 overflow-visible bg-linear-to-br from-[#1B1A3D] via-[#2A285D] to-[#101426] px-6 py-12 text-white sm:px-8 sm:py-16 lg:px-12">
          <div className="pointer-events-none absolute -right-24 -top-20 h-96 w-96 rounded-full bg-[#E10E19]/20 blur-[100px]" />
          <div className="pointer-events-none absolute -left-20 bottom-0 h-80 w-80 rounded-full bg-[#2563EB]/15 blur-[100px]" />

          <div className="relative mx-auto grid w-full max-w-[1200px] items-center gap-6 lg:grid-cols-[1fr_1fr]">
            <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                Aplicación móvil
              </p>
              <h2 className="text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-[2.75rem]">
                Descarga la app y{" "}
                <span className="bg-linear-to-r from-[#FFD44D] to-[#FFB020] bg-clip-text text-transparent">
                  compra más rápido
                </span>
              </h2>

              <p className="mt-4 max-w-[44ch] text-base leading-relaxed text-white/70">
                Revisa inventario, cotiza tu inicial y solicita financiamiento desde tu celular en minutos.
              </p>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                {/* App Store badge */}
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-14 items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-5 transition-all hover:border-white/30 hover:bg-white/10"
                >
                  <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  <div className="flex flex-col">
                    <span className="text-[10px] leading-none text-white/60">Descárgala en</span>
                    <span className="text-base font-semibold leading-tight">App Store</span>
                  </div>
                </a>

                {/* Google Play badge */}
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-14 items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-5 transition-all hover:border-white/30 hover:bg-white/10"
                >
                  <svg viewBox="0 0 24 24" className="h-7 w-7">
                    <path d="M3.61 1.814L13.793 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .61-.92z" fill="#4285F4" />
                    <path d="M16.657 8.893L5.536.907A1.005 1.005 0 0 0 4.15 1.05l10.51 10.513 1.997-2.67z" fill="#EA4335" />
                    <path d="M16.657 15.107L14.66 12.44 4.15 22.95a1.005 1.005 0 0 0 1.386.142l11.121-7.985z" fill="#34A853" />
                    <path d="M20.89 10.627l-3.196-1.82L15.46 12l2.234 3.193 3.196-1.82a1.49 1.49 0 0 0 0-2.746z" fill="#FBBC05" />
                  </svg>
                  <div className="flex flex-col">
                    <span className="text-[10px] leading-none text-white/60">Disponible en</span>
                    <span className="text-base font-semibold leading-tight">Google Play</span>
                  </div>
                </a>
              </div>
            </div>

            <motion.div
              className="relative flex min-h-[260px] items-center justify-center overflow-hidden lg:min-h-[300px]"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <img
                src="/appmovil.avif"
                alt="Aplicación móvil Oriental Ramirez"
                className="pointer-events-none absolute left-1/2 top-1/2 h-auto w-[280px] max-w-[90%] -translate-x-1/2 -translate-y-[55%] drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)] sm:w-[480px] sm:max-w-none lg:w-[560px] lg:-translate-y-[56%]"
              />
            </motion.div>
          </div>
        </section>

        <section className="mb-8 rounded-3xl border border-white/20 bg-linear-to-b from-white/90 to-white/75 shadow-2xl backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="rounded-2xl"
          >
            <FeaturedVehicles onClick={handleVehicleClick} />
          </motion.div>
        </section>

        <section className="relative overflow-hidden rounded-3xl border border-white/15 bg-linear-to-r from-[#111827] via-[#1F2937] to-[#0B1020] p-6 text-white shadow-2xl sm:p-8">
          <div className="absolute -right-10 -top-12 h-48 w-48 rounded-full bg-[#E10E19]/20 blur-3xl" />
          <p className="text-xs uppercase tracking-[0.22em] text-white/70">
            Navegacion Rapida
          </p>
          <h2 className="mt-2 max-w-xl text-2xl font-bold sm:text-3xl">
            Recorre todo el inventario y encuentra moto o carro en minutos.
          </h2>
          <Button
            className="mt-5 h-11 rounded-full bg-[#E10E19] px-6 text-white hover:bg-[#C40D17]"
            onClick={() => navigate("/catalogo")}
          >
            Ir al Catalogo Completo
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </section>
      </main>

    </div>
  );
}

